import { useApolloClient } from "@apollo/client";
import { faFileExport } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MenuItemWithIcon } from "@msys/ui";
import { CircularProgress, Icon } from "@mui/material";
import * as Sentry from "@sentry/react";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import React, { useState } from "react";
import {
  useExportProjectToPdsMutation,
  useExportProjectToPds_PdsIntegrationQuery,
} from "./ExportProjectToPds.generated.js";

type Props = {
  projectId: string;
  refetchQueries?: string[];
};

export const ExportProjectToPdsMenuItem = ({
  projectId,
  refetchQueries,
}: Props) => {
  const client = useApolloClient();
  const { t } = useTranslate("Projects");
  const { enqueueSnackbar } = useSnackbar();
  const [status, setStatus] = useState<"idle" | "loading" | "error">("idle");

  const [exportProjectToPds] = useExportProjectToPdsMutation({ client });

  const pdsIntegrationQuery = useExportProjectToPds_PdsIntegrationQuery({
    client,
  });

  const isPdsProjectExportEnabled = React.useMemo(() => {
    const pdsIntegration =
      pdsIntegrationQuery.data?.organisationIntegration.__typename ===
      "OrganisationIntegrationResult"
        ? pdsIntegrationQuery.data.organisationIntegration
            .organisationIntegration
        : null;

    if (!pdsIntegration) return false;

    const servicesRaw = (pdsIntegration.settings as any)?.services;

    const services: unknown[] = Array.isArray(servicesRaw)
      ? servicesRaw
      : typeof servicesRaw === "string"
        ? (() => {
            try {
              const parsed = JSON.parse(servicesRaw);
              return Array.isArray(parsed) ? parsed : [];
            } catch {
              return [];
            }
          })()
        : [];

    return services.some(
      service =>
        typeof service === "string" &&
        service.trim().toUpperCase() === "PROJECT_EXPORT"
    );
  }, [pdsIntegrationQuery.data]);

  if (!isPdsProjectExportEnabled) return null;

  return (
    <MenuItemWithIcon
      icon={
        status !== "loading" ? (
          <Icon>
            <FontAwesomeIcon icon={faFileExport} />
          </Icon>
        ) : (
          <CircularProgress size={"1rem"} />
        )
      }
      onClick={async () => {
        setStatus("loading");

        let hasHandledError = false;
        let completedNotified = false;

        const notifyCompleted = () => {
          if (hasHandledError || completedNotified) return;
          completedNotified = true;
          setStatus("idle");
          enqueueSnackbar(t("PDS export finished"), { variant: "success" });
        };

        const handleError = (e: unknown) => {
          if (hasHandledError) return;
          hasHandledError = true;
          if (e instanceof Error)
            enqueueSnackbar(e.message, { variant: "error" });
          Sentry.captureException(e);
          setStatus("error");
        };

        const mutationPromise = exportProjectToPds({
          variables: { projectId },
          refetchQueries,
        });

        try {
          await mutationPromise;
        } catch (e) {
          handleError(e);
          return;
        }

        if (hasHandledError || completedNotified) {
          return;
        }

        enqueueSnackbar(t("PDS export is running in the background"), {
          variant: "success",
        });
        setStatus("idle");
      }}
      disabled={status === "loading"}
    >
      {t("Export quotes to PDS")}
    </MenuItemWithIcon>
  );
};
